import classNames from 'classnames'
import React, { PropsWithChildren, RefObject, useCallback, useEffect, useState } from 'react'

import { SpacingType } from '@ui/design-system/spacing'

import styles from './FullScreenFooter.module.scss'

interface Props extends PropsWithChildren {
  gap?: SpacingType
  contentRef?: RefObject<HTMLDivElement | null>
  fullWidth?: boolean
}

export const FullScreenFooter: React.FC<Props> = ({ children, contentRef, fullWidth }) => {
  const [hasShadow, setHasShadow] = useState(false)

  const updateShadow = useCallback(() => {
    const contentEl = contentRef?.current
    const container = (contentEl?.parentElement as HTMLElement) || document.documentElement

    const scrollTop = container.scrollTop
    const clientHeight = container.clientHeight
    const scrollHeight = container.scrollHeight

    const scrollableDistance = Math.max(0, scrollHeight - clientHeight)
    const canScroll = scrollableDistance > 8
    const isAtBottom = scrollableDistance - scrollTop <= 4

    setHasShadow(canScroll && !isAtBottom)
  }, [contentRef])

  useEffect(() => {
    const contentEl = contentRef?.current
    const scrollContainerEl = contentEl?.parentElement ?? null
    const scrollContainer = scrollContainerEl ?? window
    let rafId = 0
    let nestedRafId = 0

    const scheduleShadowUpdate = () => {
      window.cancelAnimationFrame(rafId)
      window.cancelAnimationFrame(nestedRafId)

      rafId = window.requestAnimationFrame(() => {
        updateShadow()
        nestedRafId = window.requestAnimationFrame(updateShadow)
      })
    }

    scrollContainer.addEventListener('scroll', updateShadow, { passive: true })
    window.addEventListener('resize', updateShadow, { passive: true })

    const resizeObserver = new ResizeObserver(() => {
      scheduleShadowUpdate()
    })

    const mutationObserver = new MutationObserver(() => {
      scheduleShadowUpdate()
    })

    resizeObserver.observe(document.documentElement)

    if (contentEl) {
      resizeObserver.observe(contentEl)
      mutationObserver.observe(contentEl, { childList: true, subtree: true })
    }

    if (scrollContainerEl) {
      resizeObserver.observe(scrollContainerEl)
    }

    scheduleShadowUpdate()
    updateShadow()

    return () => {
      scrollContainer.removeEventListener('scroll', updateShadow)
      window.removeEventListener('resize', updateShadow)
      window.cancelAnimationFrame(rafId)
      window.cancelAnimationFrame(nestedRafId)
      mutationObserver.disconnect()
      resizeObserver.disconnect()
    }
  }, [contentRef, updateShadow])

  return (
    <div
      className={classNames(styles.fullScreenFooter, {
        [styles.hasShadow]: hasShadow,
        [styles.notSticky]: !contentRef,
        [styles.fullWidth]: fullWidth,
      })}
    >
      {children}
    </div>
  )
}
